import { useState, useMemo, useRef, useEffect, useId } from 'react'
import { ChevronsUpDown, Search, Check, ArrowDownAZ, Clock } from 'lucide-react'
import { searchProjects, projectOptionLabel, type ProjectSortMode } from '@trackmind/core'
import type { Project } from '@/types'
import { cn } from '@/lib/utils'

const SORT_KEY = 'trackmind.projectPicker.sort'

function loadSort(): ProjectSortMode {
  try {
    return localStorage.getItem(SORT_KEY) === 'recent' ? 'recent' : 'alpha'
  } catch {
    // Private mode / storage disabled. A default is fine; this is a preference,
    // not data.
    return 'alpha'
  }
}

export interface ProjectPickerProps {
  projects: Project[]
  value: string | undefined
  onChange: (projectId: string) => void
  /** Which statuses may be chosen. Defaults to active only. */
  statuses?: readonly string[]
  placeholder?: string
  /** Rendered above the list — used by the parent picker for "No parent". */
  emptyOption?: { label: string; value: string }
  disabled?: boolean
  className?: string
  /** Overrides the trigger's height/typography for inline, compact uses. */
  triggerClassName?: string
  id?: string
  'aria-label'?: string
}

/**
 * Picking a project when you have more than a screenful.
 *
 * Every project picker in the app was a bare `<Select>` — no search, and no
 * sort of any kind, so the options came back in whatever order IndexedDB
 * returned them. That's uuid order: random, but *stable*, so it looked
 * intentional. Past about fifteen projects choosing one meant reading the whole
 * list, which is the slow part of capture, not the typing.
 *
 * Built without a new dependency on purpose. There's no combobox primitive
 * here and no popover package; adding cmdk or Radix Popover to put a text box
 * above a list is a lot of bundle for one control (§4.5). Radix's `Select`
 * can't hold an input, and its `DropdownMenu` eats keystrokes for its own
 * typeahead, so this is a plain button, input and listbox with the ARIA
 * wiring done by hand.
 *
 * Two views: the same control, but the phone gets 44px rows and a 16px input
 * (below 16px iOS zooms the whole page on focus), and drops the keyboard hint
 * — there is no Esc key on a touch keyboard. The list is capped and scrolls
 * rather than growing, so it never pushes a form off screen.
 */
export function ProjectPicker({
  projects,
  value,
  onChange,
  statuses,
  placeholder = 'Select a project',
  emptyOption,
  disabled,
  className,
  triggerClassName,
  id,
  'aria-label': ariaLabel,
}: ProjectPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<ProjectSortMode>(loadSort)
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  const options = useMemo(
    () => searchProjects(projects, {
      query,
      sort,
      statuses,
      // Keeps the current selection visible even when it's archived, paused or
      // filtered out by the query — without this the field reads empty and
      // saving the form silently moves the task.
      alwaysIncludeId: value ?? null,
    }),
    [projects, query, sort, statuses, value],
  )

  const rows = useMemo(
    () => (emptyOption ? [{ id: emptyOption.value, name: emptyOption.label, path: '', status: 'active', score: 0 }] : [])
      .concat(options),
    [emptyOption, options],
  )

  const selected = options.find((o) => o.id === value)
  const label = value === emptyOption?.value && emptyOption
    ? emptyOption.label
    : projectOptionLabel(selected)

  // Close on an outside press. Pointerdown rather than click so a press that
  // starts outside and ends inside doesn't leave it open.
  useEffect(() => {
    if (!open) return
    const onDown = (event: PointerEvent) => {
      // setOpen, not setOpenState: closing needs no reset, and the reset
      // helper is declared below this effect.
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  // Focusing is a DOM effect and belongs here. Resetting the query and the
  // highlight does NOT — doing it in an effect keyed on `open` costs an extra
  // render pass each time and trips the React Compiler's setState-in-effect
  // rule, so both live in the open/close handlers below.
  useEffect(() => {
    if (open) inputRef.current?.focus()
  }, [open])

  // Narrowing the list can strand the highlight past the end. Clamp on read
  // rather than resetting from an effect.
  const activeIndex = Math.min(active, Math.max(0, rows.length - 1))

  const setOpenState = (next: boolean) => {
    if (next) { setQuery(''); setActive(0) }
    setOpen(next)
  }

  const commit = (projectId: string) => {
    onChange(projectId)
    setOpenState(false)
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive(Math.min(activeIndex + 1, rows.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive(Math.max(activeIndex - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const row = rows[activeIndex]
      if (row) commit(row.id)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setOpenState(false)
    }
  }

  const toggleSort = () => {
    const next: ProjectSortMode = sort === 'alpha' ? 'recent' : 'alpha'
    setSort(next)
    setActive(0)
    try { localStorage.setItem(SORT_KEY, next) } catch { /* preference only */ }
  }

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel ?? placeholder}
        onClick={() => setOpenState(!open)}
        className={cn(
          'flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input',
          'bg-background px-3 py-2 text-sm ring-offset-background',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          triggerClassName,
        )}
      >
        <span className={cn('truncate text-left', !label && 'text-muted-foreground')}>
          {label || placeholder}
        </span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
      </button>

      {open && (
        <div
          className={cn(
            'absolute z-50 mt-1 w-full min-w-[220px] rounded-md border bg-popover shadow-md',
            'text-popover-foreground',
          )}
        >
          <div className="flex items-center gap-2 border-b px-2">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActive(0) }}
              onKeyDown={onKeyDown}
              placeholder="Search projects…"
              aria-controls={listId}
              aria-autocomplete="list"
              // 16px on phones: anything smaller and iOS zooms the page on
              // focus and never zooms back out.
              className="h-11 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground md:h-9 md:text-sm"
            />
            <button
              type="button"
              onClick={toggleSort}
              title={sort === 'alpha' ? 'Sorted A–Z — switch to most recent' : 'Sorted by most recent — switch to A–Z'}
              aria-label={sort === 'alpha' ? 'Sort by most recent' : 'Sort alphabetically'}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded hover:bg-accent"
            >
              {sort === 'alpha'
                ? <ArrowDownAZ className="h-4 w-4 opacity-70" />
                : <Clock className="h-4 w-4 opacity-70" />}
            </button>
          </div>

          <ul id={listId} role="listbox" className="max-h-64 overflow-y-auto overscroll-contain py-1">
            {rows.length === 0 && (
              <li className="px-3 py-3 text-sm text-muted-foreground">
                No project matches “{query}”.
              </li>
            )}
            {rows.map((row, index) => (
              <li key={row.id} role="option" aria-selected={row.id === value}>
                <button
                  type="button"
                  onClick={() => commit(row.id)}
                  onMouseEnter={() => setActive(index)}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 text-left text-sm',
                    // 44px touch target; the desktop list stays tighter so more
                    // of it fits on screen at once.
                    'min-h-[44px] md:min-h-0 md:py-1.5',
                    index === activeIndex && 'bg-accent',
                  )}
                >
                  <Check className={cn('h-4 w-4 shrink-0', row.id === value ? 'opacity-100' : 'opacity-0')} />
                  <span className="min-w-0 flex-1 truncate">
                    {row.path && <span className="text-muted-foreground">{row.path} › </span>}
                    {row.name}
                  </span>
                  {row.status !== 'active' && (
                    <span className="shrink-0 text-xs text-muted-foreground">{row.status}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          {/* No Esc or arrow keys on a touch keyboard. */}
          <div className="hidden border-t px-3 py-1 text-[10px] text-muted-foreground md:block">
            ↑↓ to move · Enter to choose · Esc to close
          </div>
        </div>
      )}
    </div>
  )
}
